import type {
  ContractArtifactType,
  ContractDocumentDownloadView,
  ContractVersionArtifactView,
  ContractVersionPreviewView,
} from "@closer/shared";
import {
  CONTRACT_ARTIFACT_LABELS,
  formatFileSize,
  PREVIEW_EXTRACTION_STATUS_NAMES,
  PREVIEW_FALLBACK_NOTES,
} from "@closer/shared";
import { Badge, Button, Card, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";

import { ContractBlockText } from "../../components/ContractBlockText";
import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * BELGE GÖRÜNTÜLEME VE İNDİRME (CLOS-120 `[101]`).
 *
 * ### ⚠ İmzalı adres EKRANDA DURMUYOR
 *
 * Kart: *"hassas dosya URL'si KALICI veya tahmin edilebilir biçimde açığa çıkmaz."* Bu
 * yüzden indirme bir `<a href={imzaliUrl}>` DEĞİL: öyle olsaydı adres, kullanıcı hiç
 * tıklamasa bile DOM'da, geliştirici araçlarında ve ekran paylaşımında dururdu. Adres
 * yalnız tıklandığı an isteniyor, kullanılıyor ve bırakılıyor — state'te de saklanmıyor.
 *
 * ### ⚠ Ön izleme kaynağın YERİNE GEÇMİYOR
 *
 * Ekranda okunan metin `[098]`'in türevi; indirilen dosya versiyonun değişmez kaynağı.
 * Dönüşümde kaybolanlar (tablo, numaralandırma, biçim) AÇIKÇA yazılıyor — sessiz bir kayıp,
 * kullanıcıya ekrandaki metnin sözleşmenin tamamı olduğunu düşündürürdü.
 *
 * ### ⚠ Ön izleme yoksa ekran BOŞ KALMIYOR
 *
 * Kart: *"ön izleme başarısızsa kaynak dosya veya güvenli hata seçeneği sunulur."* Taranmış
 * bir PDF ya da okunamayan bir belgede indirme yolu açık kalıyor; OCR'ın yokluğu belgeye
 * ulaşmayı engellemiyor (kartın runtime kuralı: OCR hard blocker değildir).
 */

function Satir({ etiket, deger }: { etiket: string; deger: React.ReactNode }) {
  return (
    <div className="flex flex-wrap gap-2 text-sm">
      <dt className="w-40 shrink-0 text-fg-soft">{etiket}</dt>
      <dd className="flex-1 text-fg">{deger}</dd>
    </div>
  );
}

export function ContractDocumentPage() {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const { id = "", versionId = "" } = useParams();
  const toast = useToast();

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "contract", id, "versiyon", versionId, "belge"),
    queryFn: async () =>
      (
        await api<ContractVersionPreviewView>(
          `/api/contracts/${id}/versions/${versionId}/preview`,
          { tenantId },
        )
      ).data,
  });

  /**
   * ⚠ Bilet bir MUTATION gibi ele alınıyor, sorgu gibi değil: önbelleğe alınabilir bir
   * sorgu olsaydı süresi dolmuş bir adres tekrar tekrar sunulur, erişimi kaldırılmış bir
   * kullanıcıya da çalışan bir bağlantı kalırdı.
   */
  /**
   * ⚠ İndirme bir `<a href>` DEĞİL, tıklama anında üretilen geçici bir bağlantı: adres
   * sayfada dursaydı kullanıcı hiç indirmese bile üretilmiş olurdu (dosyanın başındaki not).
   * Üç indirme yolu (kaynak, temiz sürüm, redline) aynı davranışı paylaşıyor.
   */
  const baglantiyiAc = (bilet: ContractDocumentDownloadView) => {
    const bag = document.createElement("a");
    bag.href = bilet.downloadUrl;
    bag.rel = "noopener noreferrer";
    if (bilet.fileName) bag.download = bilet.fileName;
    document.body.appendChild(bag);
    bag.click();
    bag.remove();
  };

  const indirme = useMutation({
    mutationFn: async () =>
      (
        await api<ContractDocumentDownloadView>(
          `/api/contracts/${id}/versions/${versionId}/download-url`,
          { tenantId },
        )
      ).data,
    onSuccess: baglantiyiAc,
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Dosya indirilemedi"),
  });

  /**
   * ÜRETİLEN BELGELER (CLOS-1236 — Kanon §7.1, §7.2).
   *
   * ⚠ Liste SUNUCUDAN geliyor, tahminden değil. "Versiyon varsa temiz sürümü de vardır"
   * diye varsaysaydık, CLOS-1232 öncesi dondurulmuş versiyonlarda düğme kullanıcıyı
   * çalışmayan bir indirmeye götürürdü — ve hatayı ancak tıkladıktan sonra görürdü.
   */
  const artifactlar = useQuery({
    queryKey: tenantKey(tenantId, "contract", id, "versiyon", versionId, "artifact"),
    queryFn: async () =>
      (
        await api<ContractVersionArtifactView[]>(
          `/api/contracts/${id}/versions/${versionId}/artifacts`,
          { tenantId },
        )
      ).data,
  });

  /**
   * ⚠ Aynı indirme deseni: bilet bir MUTATION. Türü parametre alıyor ki iki düğme tek
   * mantığı paylaşsın; iki ayrı mutation yazsaydık biri güncellenip öteki kalırdı.
   */
  const artifactIndir = useMutation({
    mutationFn: async (tur: ContractArtifactType) =>
      (
        await api<ContractDocumentDownloadView>(
          `/api/contracts/${id}/versions/${versionId}/artifacts/${tur}/download-url`,
          { tenantId },
        )
      ).data,
    onSuccess: baglantiyiAc,
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Belge indirilemedi"),
  });

  /**
   * MATERYALİZASYON (CLOS-1238 — Kanon §29).
   *
   * ⚠ AÇIK bir eylem: kanon otomatik toplu dönüşümü yasaklıyor. Kullanıcı hangi versiyonun
   * Word belgesini istediğine kendisi karar veriyor.
   */
  const materyalize = useMutation({
    mutationFn: async () =>
      (
        await api<{ artifactType: ContractArtifactType }>(
          `/api/contracts/${id}/versions/${versionId}/artifacts/materialized`,
          { tenantId, method: "POST" },
        )
      ).data,
    onSuccess: async () => {
      toast.success("Word belgesi üretildi");
      await artifactlar.refetch();
    },
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Belge üretilemedi"),
  });

  if (sorgu.isPending) {
    return (
      <div role="status" aria-label="Belge yükleniyor">
        <Skeleton lines={6} />
      </div>
    );
  }

  if (sorgu.error || !sorgu.data) {
    return (
      <div className="flex flex-col gap-4">
        <StateView
          state={resolveErrorState(sorgu.error, {
            idempotent: true,
            overrides: {
              forbiddenOrMissing: {
                title: "Belge bulunamadı",
                description: "Bu versiyon yok ya da erişiminiz bulunmuyor.",
              },
            },
          })}
          onRetry={() => void sorgu.refetch()}
        />
        <Link
          className="text-sm font-medium text-primary hover:underline"
          to={`/app/sozlesmeler/${id}/versiyonlar`}
        >
          Versiyon geçmişine dön
        </Link>
      </div>
    );
  }

  const onizleme = sorgu.data;
  const durum = onizleme.extraction.status;
  const metin = durum === "extracted" ? onizleme.extraction.document : null;
  /** Kaynak kaldırılmışsa indirilecek bir şey yok; düğmeyi göstermek boş bir söz olurdu. */
  const indirilebilir = onizleme.source.available;
  /**
   * ⚠ Metin çıkmışsa "başarısız" notu YOK; çıkmamışsa mutlaka bir not var. Tür bunu
   * zorluyor: `extracted` sözlükte hiç yer almıyor, yani sessiz bir boşluk kalamaz.
   */
  const dusumNotu = durum === "extracted" ? null : PREVIEW_FALLBACK_NOTES[durum];

  return (
    <div className="flex w-full flex-col gap-5">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto text-2xl font-bold text-fg-heading sm:text-3xl">
          Belge — Versiyon v{onizleme.versionNumber}
        </h1>
        <Badge tone={durum === "extracted" ? "success" : "warning"}>
          {PREVIEW_EXTRACTION_STATUS_NAMES[durum]}
        </Badge>
      </header>

      {/*
        ⚠ Kart yalnız ARTIFACT VARSA çiziliyor. Boş bir "Üretilen belgeler" kartı, eski
        versiyonlarda kullanıcıya olmayan bir özelliği vaat ederdi.
      */}
      {/*
        ⚠ Düğme yalnız METNİ OLAN ve HENÜZ BELGESİ ÜRETİLMEMİŞ versiyonda. Metni olmayan
        (dosya kaynaklı) versiyonda sunucu zaten 409 döner; düğmeyi göstermek kullanıcıyı
        hata almaya göndermek olurdu.
      */}
      {metin !== null && (artifactlar.data?.length ?? 0) === 0 && (
        <Card title="Word belgesi">
          <p className="text-sm text-fg-muted">
            Bu versiyonun metni uygulama içinde saklanıyor. İsterseniz metinden bir Word belgesi
            üretebilirsiniz; versiyonun kendisi değişmez.
          </p>
          <Button
            className="mt-3"
            variant="secondary"
            onClick={() => materyalize.mutate()}
            disabled={materyalize.isPending}
          >
            {materyalize.isPending ? "Üretiliyor…" : "Word belgesi üret"}
          </Button>
        </Card>
      )}

      {(artifactlar.data?.length ?? 0) > 0 && (
        <Card title="Üretilen belgeler">
          <div className="flex flex-wrap gap-2">
            {artifactlar.data!.map((a) => (
              <Button
                key={a.artifactType}
                variant="secondary"
                onClick={() => artifactIndir.mutate(a.artifactType)}
                disabled={artifactIndir.isPending}
                aria-label={`${CONTRACT_ARTIFACT_LABELS[a.artifactType]} indir`}
              >
                {CONTRACT_ARTIFACT_LABELS[a.artifactType]}
              </Button>
            ))}
          </div>
          {/*
            ⚠ Kullanıcıya "canonical"/"redline" DENMİYOR (kanon §28.2: normal kullanıcıya
            teknik kavram gösterilmez). Cümle ne olduğunu anlatıyor, terimi değil.
          */}
          <p className="mt-3 text-xs text-fg-faint">
            Temiz Sürüm bu versiyonun tam metnidir. Değişiklikleri Göster, bir önceki versiyona göre
            farkı Word'ün kendi değişiklik izleme biçiminde açar.
          </p>
        </Card>
      )}

      <Card
        title="Kaynak dosya"
        actions={
          indirilebilir ? (
            <Button
              onClick={() => indirme.mutate()}
              disabled={indirme.isPending}
              aria-label="Kaynak dosyayı indir"
            >
              {indirme.isPending ? "Hazırlanıyor…" : "İndir"}
            </Button>
          ) : undefined
        }
      >
        <dl className="flex flex-col gap-1">
          <Satir etiket="Dosya adı" deger={onizleme.source.fileName ?? "—"} />
          <Satir etiket="Biçim" deger={onizleme.source.contentType ?? "—"} />
          <Satir etiket="Boyut" deger={formatFileSize(onizleme.source.sizeBytes)} />
          <Satir
            etiket="İçerik durumu"
            deger={indirilebilir ? "Erişilebilir" : "Kaynak dosya kaldırıldı"}
          />
          {onizleme.extraction.pageCount !== null && (
            <Satir etiket="Sayfa sayısı" deger={String(onizleme.extraction.pageCount)} />
          )}
        </dl>
        {/*
          ⚠ Süreli bağlantı KULLANICIYA da söyleniyor: bağlantıyı kopyalayıp paylaşan biri,
          onun ne kadar yaşadığını bilmeli.
        */}
        {indirilebilir && (
          <p className="mt-3 text-xs text-fg-faint">
            İndirme bağlantısı kısa süre geçerlidir; her indirmede yeniden istenir.
          </p>
        )}
      </Card>

      {/*
        OCR SONUCU (CLOS-229 `[099]`).

        ⚠ Sunucu OCR durumunu, sayfa sayısını ve güven ölçüsünü zaten üretiyordu ama ekranda
        HİÇBİR YERDE görünmüyordu: taranmış bir sözleşme yükleyen kullanıcı OCR'ın koşup
        koşmadığını, çıkan metne ne kadar güvenilebileceğini göremiyordu.

        ⚠ Kartın iki kuralı burada karşılanıyor: *"düşük güvenli alanlar kullanıcıya
        işaretlenir"* ve *"OCR sonucu otomatik hukuki gerçek, onaylı metin veya kaynak
        belgenin yerine geçen içerik sayılmaz."* İkincisi bir cümle değil bir sınır: metin
        gösteriliyor ama kaynağın yerine geçtiği ima edilmiyor.
      */}
      {onizleme.ocr && (
        <Card title="Taranmış belge okuması">
          <dl className="flex flex-col gap-1">
            <Satir etiket="Durum" deger={onizleme.ocr.status} />
            {onizleme.ocr.pageCount !== null && (
              <Satir etiket="Okunan sayfa" deger={String(onizleme.ocr.pageCount)} />
            )}
            {onizleme.ocr.averageConfidence !== null && (
              <Satir
                etiket="Ortalama güven"
                deger={`%${Math.round(onizleme.ocr.averageConfidence * 100)}`}
              />
            )}
          </dl>

          {onizleme.ocr.lowConfidenceCount > 0 && (
            <p role="alert" className="mt-3 rounded-md border border-warning p-2 text-sm text-fg">
              {onizleme.ocr.lowConfidenceCount} sayfada okuma güveni düşük; bu sayfaları kaynak
              dosyadan doğrulayın.
            </p>
          )}

          <p className="mt-3 text-xs text-fg-faint">
            Bu metin taranmış belgeden otomatik okundu. Hukuki olarak kaynak belge geçerlidir; okuma
            yalnız arama ve inceleme içindir.
          </p>
        </Card>
      )}

      {/*
        ARANABİLİR METİN SINIRI (CLOS-237 `[100]`).

        ⚠ Sunucu `searchableText` bayrağını üretiyordu ama ekran onu hiç okumuyordu:
        kullanıcı bir belgede neden arama yapamadığını ya da neden karşılaştıramadığını
        göremiyordu. Kartın kuralı açık: taranmış PDF'in kaynağı OCR olmadan da
        görüntülenir, ama aranabilir metin ve metin tabanlı karşılaştırma YALNIZ okuma
        sonucu varsa sunulur.

        ⚠ Not yalnız EKSİKKEN çıkıyor. "Arama yapılabilir" demek, çalışan bir şeyi
        duyurmak olurdu; kullanıcıya lazım olan bilgi çalışmayanın sebebidir.
      */}
      {!onizleme.searchableText && (
        <Card title="Metin araması">
          <p className="text-sm text-fg-soft">
            Bu belgede metin araması ve metin tabanlı karşılaştırma yapılamıyor
            {onizleme.ocr
              ? "; taranmış belge okuması metnin tamamını çıkaramadı."
              : ": belgede metin katmanı yok ve taranmış belge okuması yapılmamış."}
          </p>
          {onizleme.source.available && (
            <p className="mt-2 text-xs text-fg-faint">
              Kaynak dosyayı indirerek belgeyi olduğu gibi görüntüleyebilirsiniz.
            </p>
          )}
        </Card>
      )}

      {onizleme.extraction.lossNames.length > 0 && (
        <Card title="Ön izlemede korunmayanlar">
          {/* Kayıplar LİSTE: tek cümlede toplamak, hangi yapının kaybolduğunu belirsizleştirirdi. */}
          <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-fg-soft">
            {onizleme.extraction.lossNames.map((ad) => (
              <li key={ad}>{ad}</li>
            ))}
          </ul>
        </Card>
      )}

      <Card title="Ön izleme">
        {metin ? (
          <div className="flex flex-col gap-4">
            {metin.clauses.map((madde) => (
              <section key={madde.id} className="flex flex-col gap-1">
                {madde.title && (
                  <h2 className="text-base font-semibold text-fg-heading">{madde.title}</h2>
                )}
                {/*
                  ⚠ ÇİZİM ORTAK BİLEŞENDE (ADR-0062): blok artık başlık, numaralı madde,
                  hizalama ve karakter işaretleri taşıyabiliyor. Burada `blok.text`'i düz
                  basmaya devam etseydik, içe aktarılan bir DOCX'in başlığı editörde başlık,
                  ön izlemede düz metin görünürdü — kullanıcı hangisinin doğru olduğunu
                  bilemezdi.
                */}
                {madde.blocks.map((blok, i) => (
                  <ContractBlockText key={i} blok={blok} />
                ))}
              </section>
            ))}
          </div>
        ) : (
          <p className="text-sm text-fg-soft">{dusumNotu}</p>
        )}
      </Card>

      <div className="flex flex-wrap items-center gap-4">
        <Link
          className="text-sm font-medium text-primary hover:underline"
          to={`/app/sozlesmeler/${id}/versiyonlar/${versionId}`}
        >
          Versiyon detayına dön
        </Link>
        {/*
          ⚠ DOCX EDİTÖRÜ AYRI ROTADA (CLOS-1252 — ADR-0070). Bu sayfa ön izleme: kanonik
          yapıyı okuyor ve DOCX motoru yüklemiyor. Editör çekirdeği ~3,3 MB; buraya gömseydik
          yalnız metne bakan kullanıcı da o yükü indirirdi.

          ⚠ Bağlantı YETKİYLE GİZLENMİYOR: editör yetkisiz kullanıcıda salt görüntüleme
          modunda açılıyor ve bu bilinçli — "göremezsin" ile "değiştiremezsin" farklı
          cevaplar.
        */}
        <Link
          className="text-sm font-medium text-primary hover:underline"
          to={`/app/sozlesmeler/${id}/versiyonlar/${versionId}/editor`}
        >
          DOCX editöründe aç
        </Link>
      </div>
    </div>
  );
}
